import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { AlertTriangle, ChevronRight } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { TrustPanel } from '@/components/ui/TrustPanel'
import { DEMO_BULK_QUERY_RESULT, DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'
import { evidenceItemView, type TopEvidence } from '@/lib/twin-panel/evidenceView'

interface BulkQueryResult {
  query_id: string
  question_text: string
  status?: string
  twins_used: number
  twins_excluded: number
  twins_abstained?: number
  distribution: Record<string, number>
  per_segment: Record<string, Record<string, number>>
  ensemble_consensus: number
  ensemble_diversity: number
  confidence_band: { lower: number; upper: number }
  headline?: string
  top_evidence?: TopEvidence[]
}

const DEMO_EVIDENCE: TopEvidence[] = [
  {
    twin_id: 'twin_demo_4831',
    respondent_label: 'respondent_4831',
    segment: 'Recent opt-outs',
    quote: 'Whenever they give a real reason for the change, I feel respected and am less likely to opt out entirely.',
    source_text_is_untrusted: true,
  },
  {
    twin_id: 'twin_demo_0211',
    respondent_label: 'respondent_0211',
    segment: 'High-engagement / under-35',
    quote: 'I want 12 categories, not 5 buckets. I know what I want to turn off and what I want to keep.',
    source_text_is_untrusted: true,
  },
]

function wilsonCI(k: number, n: number): [number, number] {
  if (n === 0) return [0, 0]
  const z = 1.96
  const p = k / n
  const denom = 1 + (z * z) / n
  const centre = (p + (z * z) / (2 * n)) / denom
  const spread = (z * Math.sqrt((p * (1 - p) + (z * z) / (4 * n)) / n)) / denom
  return [Math.max(0, centre - spread), Math.min(1, centre + spread)]
}

function DistributionBar({ option, fraction, n, isWinner, ciOverlap }: { option: string; fraction: number; n: number; isWinner: boolean; ciOverlap?: boolean }) {
  const [lower, upper] = wilsonCI(Math.round(fraction * n), n)
  const ciText = `(${Math.round(lower * 100)}–${Math.round(upper * 100)}%)`
  const ciBarColor = isWinner && ciOverlap ? 'bg-uncertainty-amber/30' : 'bg-slate-600/40'
  return (
    <div className="flex items-center gap-3" data-testid="distribution-bar">
      <span className="text-sm text-slate-600 w-32 shrink-0 truncate">{option}</span>
      <div className="flex-1 bg-soft-panel rounded-full h-5 overflow-hidden relative">
        <div
          className={`h-full rounded-full transition-all ${isWinner ? 'bg-echo-indigo' : 'bg-echo-indigo/30'}`}
          style={{ width: `${Math.round(fraction * 100)}%` }}
        />
        {/* CI error bar overlay */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-0.5 ${ciBarColor}`}
          style={{ left: `${Math.round(lower * 100)}%`, height: '70%' }}
        />
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-0.5 ${ciBarColor}`}
          style={{ left: `${Math.round(upper * 100)}%`, height: '70%' }}
        />
      </div>
      <span className="text-sm font-medium text-deep-ink w-28 text-right tabular-nums">
        {(fraction * 100).toFixed(0)}%{' '}
        <span className="text-xs text-slate-400 font-normal">{ciText}</span>
      </span>
      <span className="text-xs text-slate-400 w-16 text-right">n={n}</span>
    </div>
  )
}

export function BulkQueryResultPage() {
  const { projectId, queryId } = useParams<{ projectId: string; queryId: string }>()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'
    || queryId === 'bq_pref_center_demo'

  const [result, setResult] = useState<BulkQueryResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [segmentExpanded, setSegmentExpanded] = useState<string | null>(null)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    if (isDemo) {
      setResult({
        query_id: DEMO_BULK_QUERY_RESULT.query_id,
        question_text: 'Which preference-center category framing reduces opt-outs most?',
        status: 'completed',
        twins_used: DEMO_BULK_QUERY_RESULT.twins_used,
        twins_excluded: DEMO_BULK_QUERY_RESULT.twins_excluded,
        distribution: DEMO_BULK_QUERY_RESULT.distribution,
        per_segment: DEMO_BULK_QUERY_RESULT.per_segment,
        ensemble_consensus: DEMO_BULK_QUERY_RESULT.ensemble_consensus,
        ensemble_diversity: DEMO_BULK_QUERY_RESULT.ensemble_diversity,
        confidence_band: DEMO_BULK_QUERY_RESULT.confidence_band,
        headline: DEMO_BULK_QUERY_RESULT.headline,
      })
      setLoading(false)
      return
    }
    fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries/${queryId}`, { headers })
      .then(r => r.ok ? r.json() as Promise<BulkQueryResult> : null).catch(() => null)
      .then(setResult)
      .finally(() => setLoading(false))
  }, [projectId, queryId, isDemo])

  // Funnel event: first_result_viewed — fire once when a completed result first renders
  const firstResultFiredRef = useRef(false)
  useEffect(() => {
    if (isDemo || !result || result.status !== 'completed' || firstResultFiredRef.current) return
    firstResultFiredRef.current = true
    fetch(`/api/v1/projects/${projectId}/funnel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'first_result_viewed' }),
    }).catch(() => {})
  }, [result?.status, projectId, isDemo])

  // Poll every 5 s while the query is still running
  useEffect(() => {
    if (isDemo || !result || result.status !== 'running') return
    const interval = setInterval(() => {
      fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries/${queryId}`, { headers })
        .then(r => r.ok ? r.json() as Promise<BulkQueryResult> : null).catch(() => null)
        .then(setResult)
    }, 5000)
    return () => clearInterval(interval)
  }, [result?.status, projectId, queryId, isDemo])

  if (loading) return (
    <div className="p-8 space-y-3">
      <div className="text-sm text-slate-500">Running bulk query across twins...</div>
      <div className="w-48 h-1.5 bg-soft-panel rounded-full overflow-hidden">
        <div className="h-full bg-echo-indigo rounded-full w-2/3 animate-pulse" />
      </div>
    </div>
  )

  if (!result) return <div className="p-8 text-sm text-slate-500">Query not found.</div>

  const sortedOptions = Object.entries(result.distribution).sort((a, b) => b[1] - a[1])

  if (sortedOptions.length === 0) {
    const noTwinsBuilt = result.status === 'completed' && result.twins_used === 0
    return (
      <div className="p-8 space-y-4">
        <div className="text-sm text-slate-600">
          {result.status === 'running'
            ? 'Still running across twins — checking every 5 seconds…'
            : result.status === 'failed'
              ? 'This query failed before completing. Try running a new query.'
              : noTwinsBuilt
                ? 'Your twin panel hasn\'t finished building yet. Re-upload your file to restart the pipeline, then run this query again once the panel is ready.'
                : 'No results yet.'}
        </div>
        {result.status === 'running' && (
          <div className="w-48 h-1.5 bg-soft-panel rounded-full overflow-hidden">
            <div className="h-full bg-echo-indigo rounded-full w-2/3 animate-pulse" />
          </div>
        )}
        {noTwinsBuilt && (
          <Link to={`/app/projects/${projectId ?? 'demo'}/upload`}>
            <Button variant="outline" size="sm">Re-upload file &rarr;</Button>
          </Link>
        )}
      </div>
    )
  }

  const winner = sortedOptions[0][0]
  const pct = (n: number) => `${(n * 100).toFixed(0)}%`

  // Quick 260622-0p8 — when the panel largely abstained, the stored distribution is an
  // abstained guess, not a credible finding. Show an honest low-confidence state instead
  // of presenting it as a confident consensus headline.
  const lowConfidence =
    result.status === 'completed' &&
    result.twins_used > 0 &&
    (result.twins_abstained ?? 0) / result.twins_used >= 0.5

  // CI overlap detection for TooCloseToCallBanner (ASK-02)
  const [lower1, upper1] = wilsonCI(Math.round(sortedOptions[0][1] * result.twins_used), result.twins_used)
  const [lower2] = sortedOptions.length >= 2
    ? wilsonCI(Math.round(sortedOptions[1][1] * result.twins_used), result.twins_used)
    : [0, 0]
  const ciOverlap = sortedOptions.length >= 2 && upper1 > lower2

  // Find segments with notable dissent (winner differs from overall winner)
  const dissentSegments = Object.entries(result.per_segment ?? {}).filter(([_seg, dist]) => {
    const segWinner = Object.entries(dist).sort((a, b) => b[1] - a[1])[0]?.[0]
    return segWinner !== winner
  })

  // Generate headline if not present
  const headline = result.headline ?? (
    `Across ${result.twins_used.toLocaleString()} twins, ${pct(result.distribution[winner])} predict ${winner}. ` +
    (dissentSegments.length > 0 ? `Notable dissent: ${dissentSegments[0][0]}.` : 'High consensus across segments.')
  )

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Header meta */}
      <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
        <span>Bulk query result</span>
        <span>·</span>
        <span>{result.twins_used.toLocaleString()} twins</span>
        <span>·</span>
        <span>{result.twins_excluded} excluded</span>
      </div>

      {/* Low-confidence banner (Quick 260622-0p8) — shown when >=50% of used twins abstained */}
      {lowConfidence && (
        <div
          data-testid="low-confidence-banner"
          className="px-4 py-3 flex items-start gap-3 bg-amber-50/60 border border-[var(--earth-amber,#D97706)]/30 rounded-xl"
        >
          <AlertTriangle className="h-4 w-4 text-uncertainty-amber shrink-0 mt-0.5" strokeWidth={1.75} />
          <div className="space-y-1">
            <p className="text-sm text-deep-ink">
              {(result.twins_abstained ?? 0).toLocaleString()}/{result.twins_used.toLocaleString()} twins abstained — the panel isn&apos;t confident enough to answer this question reliably. Try rephrasing the question, narrowing the segment, or providing explicit answer options.
            </p>
            <Link
              to={`/app/projects/${projectId ?? 'demo'}/ask?q=${encodeURIComponent(result.question_text)}`}
            >
              <Button variant="outline" size="sm">Refine this question &rarr;</Button>
            </Link>
          </div>
        </div>
      )}

      {/* Headline summary — suppressed under low confidence so an abstained guess is never
          presented as a confident consensus finding (Quick 260622-0p8) */}
      {!lowConfidence && (
        <Card className="bg-echo-indigo/5 border-echo-indigo/20" data-testid="bulk-query-result-headline">
          <p className="text-lg font-medium text-deep-ink leading-relaxed" data-testid="pre-read-headline">{headline}</p>
          <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
            <span>Pass agreement: <strong className="text-slate-700">{result.ensemble_consensus.toFixed(2)}</strong></span>
            <span>Prediction diversity: <strong className="text-slate-700">{result.ensemble_diversity.toFixed(2)}</strong></span>
            <span>
              Confidence range (Wilson 95%):&nbsp;
              <strong className="text-slate-700 font-mono">
                {pct(result.confidence_band.lower)}–{pct(result.confidence_band.upper)}
              </strong>
            </span>
          </div>
        </Card>
      )}

      {/* TooCloseToCallBanner (ASK-02) — shown when top-2 CI bands overlap */}
      {ciOverlap && (
        <div
          data-testid="too-close-to-call-banner"
          className="px-4 py-3 flex items-start gap-3 bg-amber-50/60 border border-[var(--earth-amber,#D97706)]/30 rounded-xl"
        >
          <AlertTriangle className="h-4 w-4 text-uncertainty-amber shrink-0 mt-0.5" strokeWidth={1.75} />
          <div className="space-y-1">
            <p className="text-sm text-deep-ink">
              Too close to call — the top two options&apos; confidence intervals overlap. Consider validating with real respondents before acting on this result.
            </p>
            <Link
              to={`/app/projects/${projectId ?? 'demo'}/ask?tab=validate&q=${encodeURIComponent(result.question_text)}`}
            >
              <Button variant="outline" size="sm">Validate this question &rarr;</Button>
            </Link>
          </div>
        </div>
      )}

      {/* Distribution */}
      <div className="space-y-3" data-testid="predicted-distribution">
        <h2 className="text-base font-medium text-deep-ink">
          {lowConfidence ? 'Low-confidence distribution — most twins abstained' : 'Predicted distribution'}
        </h2>
        <div className="space-y-2.5">
          {sortedOptions.map(([option, fraction]) => (
            <DistributionBar
              key={option}
              option={option}
              fraction={fraction}
              n={Math.round(fraction * result.twins_used)}
              isWinner={option === winner}
              ciOverlap={ciOverlap}
            />
          ))}
        </div>
      </div>

      {/* Per-segment cross-tab */}
      <div className="space-y-3" data-testid="per-segment-crosstab">
        <h2 className="text-base font-medium text-deep-ink">Per-segment breakdown</h2>
        {dissentSegments.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-uncertainty-amber">
            <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            <span>{dissentSegments.length} segment{dissentSegments.length > 1 ? 's' : ''} show dissent from the panel-wide winner</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-gray">
                <th className="text-left py-2 pr-6 font-medium text-slate-500">Segment</th>
                {sortedOptions.map(([o]) => (
                  <th key={o} className="text-center py-2 pr-4 font-medium text-slate-500">{o}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(result.per_segment ?? {}).map(([seg, dist]) => {
                const segWinner = Object.entries(dist).sort((a, b) => b[1] - a[1])[0]?.[0]
                const isDissent = segWinner !== winner
                return (
                  <tr key={seg} className={`border-b border-border-gray/40 ${isDissent ? 'bg-amber-50/30' : ''}`}>
                    <td className="py-2 pr-6">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/app/projects/${projectId ?? 'demo'}/echo-panel`}
                          className="text-slate-700 hover:text-echo-indigo"
                        >
                          {seg}
                        </Link>
                        {isDissent && <AlertTriangle className="h-3.5 w-3.5 text-uncertainty-amber shrink-0" strokeWidth={1.75} />}
                      </div>
                    </td>
                    {sortedOptions.map(([o]) => (
                      <td key={o} className={`text-center py-2 pr-4 ${o === segWinner ? 'font-medium text-deep-ink' : 'text-slate-500'}`}>
                        {pct(dist[o] ?? 0)}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <Link to={`/app/projects/${projectId ?? 'demo'}/echo-panel`} className="text-sm text-echo-indigo hover:underline flex items-center gap-1">
          Drill into a segment
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} />
        </Link>
      </div>

      {/* Evidence quotes — demo uses DEMO_EVIDENCE, real projects use result.top_evidence */}
      {((isDemo ? DEMO_EVIDENCE : result.top_evidence) ?? []).length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-medium text-deep-ink">Why this prediction</h2>
          <div className="space-y-3">
            {(isDemo ? DEMO_EVIDENCE : result.top_evidence ?? []).map((ev, index) => {
              const view = evidenceItemView(ev, index, projectId)
              return (
                <div key={view.key} className="border-l-2 border-echo-indigo/30 pl-4 py-1">
                  <p className="text-sm text-slate-700 italic">"{ev.quote}"</p>
                  <div className="flex items-center gap-2 mt-1">
                    {view.kind === 'link' ? (
                      <Link
                        to={view.to!}
                        className="text-xs text-echo-indigo hover:underline"
                        data-testid="drill-into-twin"
                      >
                        {view.label}
                      </Link>
                    ) : (
                      <span className="text-xs text-slate-600">{view.label}</span>
                    )}
                    {view.segment && <span className="text-xs text-slate-400">· {view.segment}</span>}
                    <Badge variant="info" size="sm">source quote — untrusted</Badge>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* TrustPanel (AHA-02, ASK-01) — position 4: after evidence, before next actions */}
      <TrustPanel
        projectId={projectId ?? 'demo'}
        scoredCount={result.twins_used}
        totalCount={result.twins_used + result.twins_excluded}
        accuracy={result.ensemble_consensus}
        missCount={result.twins_excluded}
        abstainedCount={result.twins_abstained ?? 0}
        defaultOpen={true}
      />

      {/* Validation recommendation */}
      {dissentSegments.length > 0 && (
        <Card className="border-uncertainty-amber/30 bg-amber-50/20">
          <h2 className="text-sm font-medium text-deep-ink mb-2">Validation recommendation</h2>
          <p className="text-sm text-slate-600">
            {dissentSegments[0][0]} shows notable difference from the panel-wide winner.
            Recommend validating with 30 real respondents from this segment before shipping.
          </p>
          <Link to={`/app/projects/${projectId ?? 'demo'}/validation`} className="mt-3 inline-block">
            <Button variant="outline" size="sm">Plan validation</Button>
          </Link>
        </Card>
      )}

      {/* Drill into twins */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-border-gray">
        <Link to={`/app/projects/${projectId ?? 'demo'}/twin-panel/twins`}>
          <Button variant="outline" size="sm">Browse twin responses</Button>
        </Link>
        <Link to={`/app/projects/${projectId ?? 'demo'}/twin-panel/queries`}>
          <Button variant="ghost" size="sm">All bulk queries</Button>
        </Link>
      </div>

    </div>
  )
}
